/**
 * Slice 41 — Overview Setup checklist (status + jump). Locked canvas:
 * docs/design-archives/slice-41-unit-onboarding-checklist.bundled.html
 */
import { useState } from "react";
import { Check, Minus, ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { PropertyTabId } from "@/lib/propertyTabs";
import { useUnitSetupChecklist } from "@/hooks/useUnitSetupChecklist";
import {
  setupCompleteSummary,
  setupProgressLabel,
  setupSubtitle,
  type SetupRowId,
  type SetupRowView,
} from "@/lib/unitSetupChecklist";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

type PropertyBits = {
  owner_id?: string | null;
  entry_method?: string | null;
  entry_code?: string | null;
  entry_location?: string | null;
  access_notes?: string | null;
  amazon_buy_list_url?: string | null;
  setup_checklist_state?: unknown;
};

interface OverviewSetupChecklistProps {
  propertyId: string;
  property: PropertyBits;
  onTabChange?: (tab: PropertyTabId | string) => void;
  /** When complete, parent can still force expanded review. */
  forceExpanded?: boolean;
  onForceExpandedChange?: (open: boolean) => void;
}

function StatusIcon({ status }: { status: SetupRowView["status"] }) {
  if (status === "done") {
    return (
      <div className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-[#24a89a] max-sm:mt-0 max-sm:h-[26px] max-sm:w-[26px]">
        <Check className="h-[13px] w-[13px] stroke-[3.2] text-white" strokeLinecap="round" strokeLinejoin="round" />
      </div>
    );
  }
  if (status === "skipped") {
    return (
      <div className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 border-[#cdd8df] bg-[#f1f5f7] max-sm:mt-0 max-sm:h-[26px] max-sm:w-[26px]">
        <Minus className="h-[11px] w-[11px] stroke-[3.4] text-[#8494a2]" strokeLinecap="round" />
      </div>
    );
  }
  return (
    <div className="mt-0.5 h-[22px] w-[22px] shrink-0 rounded-full border-2 border-[#cdd8df] max-sm:mt-0 max-sm:h-[26px] max-sm:w-[26px]" />
  );
}

export function OverviewSetupChecklist({
  propertyId,
  property,
  onTabChange,
  forceExpanded = false,
  onForceExpandedChange,
}: OverviewSetupChecklistProps) {
  const {
    rows,
    doneCount,
    skippedCount,
    complete,
    openCount,
    skipRow,
    skipping,
  } = useUnitSetupChecklist(propertyId, property);

  const [skipTarget, setSkipTarget] = useState<SetupRowView | null>(null);
  const settled = doneCount + skippedCount;
  const progressPct = Math.max(2, Math.round((settled / rows.length) * 100));
  const showFullList = !complete || forceExpanded;
  const lastOpenId = rows.find((r) => r.status === "open")?.id ?? null;

  const go = (tab: PropertyTabId) => onTabChange?.(tab);

  const confirmSkip = (row: SetupRowView) => {
    skipRow(row.id as SetupRowId);
    setSkipTarget(null);
  };

  const onSkipClick = (e: React.MouseEvent, row: SetupRowView) => {
    e.stopPropagation();
    if (!row.skippable || row.status !== "open") return;
    // Phone: sheet. Desktop: same sheet for one confirmation pattern.
    setSkipTarget(row);
  };

  if (complete && !forceExpanded) {
    return (
      <div
        className="flex items-center gap-[11px] rounded-xl border border-[#e6edf1] bg-white px-4 py-3"
        data-tour="tour-overview-setup-complete"
      >
        <div className="flex h-[19px] w-[19px] shrink-0 items-center justify-center rounded-full bg-[#24a89a]">
          <Check className="h-[11px] w-[11px] stroke-[3.4] text-white" strokeLinecap="round" strokeLinejoin="round" />
        </div>
        <div className="min-w-0 flex-1 text-[13.5px] font-medium text-[#475569]">
          {setupCompleteSummary(doneCount, skippedCount)}
        </div>
        <button
          type="button"
          className="shrink-0 text-[13px] font-medium text-[#8494a2] hover:text-[#15776d]"
          onClick={() => onForceExpandedChange?.(true)}
        >
          Review setup
        </button>
      </div>
    );
  }

  return (
    <>
      <div
        className="overflow-hidden rounded-2xl border border-[#e6edf1] bg-white"
        data-tour="tour-overview-setup"
      >
        <div className="flex items-end gap-5 border-b border-[#eef3f6] px-6 pb-[18px] pt-5 max-sm:flex-col max-sm:items-stretch max-sm:gap-2.5 max-sm:px-[18px] max-sm:pb-4 max-sm:pt-[18px]">
          <div className="min-w-0 flex-1 flex flex-col gap-1">
            <div className="text-[20px] font-bold tracking-[-0.3px] text-[#0f1720] max-sm:text-[21px]">
              Setup
            </div>
            <div className="text-[13.5px] font-medium text-[#64748b]">
              {setupSubtitle(openCount, complete)}
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-[7px] max-sm:w-full max-sm:flex-row max-sm:items-center max-sm:gap-3">
            <div className="order-2 text-[13px] font-bold text-[#15776d] max-sm:order-2 max-sm:shrink-0 max-sm:text-[12.5px]">
              <span className="max-sm:hidden">{setupProgressLabel(settled)}</span>
              <span className="sm:hidden">{settled} of {rows.length}</span>
            </div>
            <div className="order-1 h-[5px] w-[180px] overflow-hidden rounded-full bg-[#e6edf1] max-sm:order-1 max-sm:w-auto max-sm:flex-1">
              <div className="h-[5px] bg-[#24a89a]" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
          {complete && forceExpanded ? (
            <button
              type="button"
              className="text-[13px] font-medium text-[#8494a2] hover:text-[#15776d] max-sm:self-start"
              onClick={() => onForceExpandedChange?.(false)}
            >
              Done reviewing
            </button>
          ) : null}
        </div>

        <div>
          {rows.map((row) => {
            const open = row.status === "open";
            const primary = open && lastOpenId === row.id && openCount === 1;
            return (
              <div
                key={row.id}
                role="button"
                tabIndex={0}
                onClick={() => go(row.tab)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    go(row.tab);
                  }
                }}
                className={cn(
                  "flex cursor-pointer items-start gap-3.5 border-b border-[#f1f5f7] px-6 py-[15px] last:border-b-0 hover:bg-[#f8fbfc]",
                  "max-sm:min-h-[76px] max-sm:items-center max-sm:px-[18px] max-sm:py-0",
                )}
              >
                <StatusIcon status={row.status} />
                <div className="min-w-0 flex-1 flex flex-col gap-0.5 max-sm:py-3">
                  <div
                    className={cn(
                      "text-[15.5px] font-bold tracking-[-0.2px]",
                      open ? "text-[#0f1720]" : "text-[#8494a2]",
                    )}
                  >
                    {row.title}
                  </div>
                  <div className="text-[12.5px] font-medium leading-snug text-[#8494a2] max-sm:line-clamp-2">
                    {row.hint}
                  </div>
                  {row.note ? (
                    <div className="pt-0.5 text-[12.5px] font-bold text-[#15776d]">{row.note}</div>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-4 pt-0.5 max-sm:pt-0">
                  {open && row.skippable ? (
                    <button
                      type="button"
                      className="hidden text-[12.5px] font-medium text-[#8494a2] hover:text-[#475569] sm:inline"
                      onClick={(e) => onSkipClick(e, row)}
                    >
                      Skip for now
                    </button>
                  ) : null}
                  {open ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        go(row.tab);
                      }}
                      className={cn(
                        "hidden h-[34px] items-center gap-[7px] rounded-full border-[1.5px] px-[15px] text-[13px] font-bold sm:inline-flex",
                        primary
                          ? "border-[#15776d] bg-[#15776d] text-white hover:bg-[#0f5f57]"
                          : "border-[#24a89a] text-[#15776d] hover:bg-[#f4fbfa]",
                      )}
                    >
                      {row.cta}
                      <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />
                    </button>
                  ) : (
                    <div className="hidden text-[13px] font-medium text-[#8494a2] sm:block">
                      {row.status === "skipped" ? "Skipped" : "Done"}
                    </div>
                  )}
                  {/* Phone: chevron / skip */}
                  <div className="flex items-center gap-2 sm:hidden">
                    {open && row.skippable ? (
                      <button
                        type="button"
                        className="text-[12.5px] font-medium text-[#8494a2]"
                        onClick={(e) => onSkipClick(e, row)}
                      >
                        Skip
                      </button>
                    ) : null}
                    <ArrowRight className="h-4 w-4 text-[#cdd8df]" strokeWidth={2.2} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Sheet open={!!skipTarget} onOpenChange={(o) => !o && setSkipTarget(null)}>
        <SheetContent side="bottom" className="rounded-t-2xl px-5 pb-8 pt-5">
          <SheetHeader className="text-left">
            <SheetTitle className="text-[19px] font-bold tracking-[-0.3px] text-[#0f1720]">
              {skipTarget
                ? `Skip ${skipTarget.title
                    .replace(/^(Add|Film|Build|Check|Record|Set|Save|Invite)\s+/i, "")
                    .toLowerCase()}?`
                : "Skip?"}
            </SheetTitle>
          </SheetHeader>
          <p className="mt-2 text-[14px] font-medium leading-relaxed text-[#64748b]">
            {skipTarget?.skipBody || "You can finish this later."}
          </p>
          <div className="mt-5 flex flex-col gap-2.5">
            <button
              type="button"
              disabled={skipping || !skipTarget}
              className="flex h-[52px] items-center justify-center rounded-[14px] bg-[#15776d] text-[16px] font-bold text-white hover:bg-[#0f5f57] disabled:opacity-60"
              onClick={() => skipTarget && confirmSkip(skipTarget)}
            >
              {skipping ? <Loader2 className="h-5 w-5 animate-spin" /> : "Skip for now"}
            </button>
            <button
              type="button"
              className="flex h-[52px] items-center justify-center rounded-[14px] border border-[#e6edf1] text-[16px] font-bold text-[#475569]"
              onClick={() => setSkipTarget(null)}
            >
              Keep going
            </button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
